import { inject, PLATFORM_ID } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  RedirectCommand,
} from "@angular/router";
import { LanguageService } from "../services/language.service";

/**
 * Replaces route parameter placeholders (e.g., :key, :isbn) in a string with actual values from route params
 */
function replaceRouteParams(
  path: string,
  params: { [key: string]: any },
): string {
  let result = path;

  // Replace each parameter placeholder with its actual value
  Object.keys(params).forEach((paramKey) => {
    const placeholder = `:${paramKey}`;
    const value = params[paramKey];

    if (value !== undefined && value !== null) {
      result = result.replace(new RegExp(placeholder, "g"), value);
    }
  });

  return result;
}

/**
 * Guard that performs a 301 redirect for old .html URLs
 * Usage:
 * - Simple redirect: { path: "news.html", canActivate: [htmlRedirectGuard], data: { redirectTo: "news" } }
 * - With route params: { path: "series/:key", canActivate: [htmlRedirectGuard], data: { redirectTo: "/series/:key" } }
 */
export const htmlRedirectGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<RedirectCommand> => {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const languageService = inject(LanguageService);

  let response: any = null;

  // Set 301 status code on server-side
  if (isPlatformServer(platformId)) {
    try {
      response = inject("RESPONSE" as any) as any;
      if (response && typeof response.status === "function") {
        response.status(301);
      }
    } catch (e) {
      console.warn("Could not set 301 status:", e);
    }
  }

  // Get redirect target from route data
  let redirectTo = route.data["redirectTo"];

  if (!redirectTo) {
    throw new Error("htmlRedirectGuard requires 'redirectTo' in route data");
  }

  // Replace route parameter placeholders with actual values
  if (typeof redirectTo === "string") {
    redirectTo = replaceRouteParams(redirectTo, route.params);
  }

  // Parse path and query parameters
  if (typeof redirectTo === "string") {
    const [path, queryString] = redirectTo.split("?");
    const pathSegments = path.split("/").filter(Boolean);

    // Parse query parameters using URLSearchParams
    const queryParams: { [key: string]: string | string[] } = {};
    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      searchParams.forEach((value, key) => {
        const allValues = searchParams.getAll(key);
        queryParams[key] = allValues.length > 1 ? allValues : value;
      });
    }

    // Get current language from LanguageService
    await languageService.initLanguage();
    const currentLang = languageService.currentLanguage();

    // Return RedirectCommand with path and query parameters
    // Include language prefix to create absolute path like /en/search
    const navigationPath = [currentLang, ...pathSegments];
    const urlTree = router.createUrlTree(navigationPath, { queryParams });

    // Set Location header for proper 301 redirect
    if (response && typeof response.setHeader === "function") {
      const locationUrl = urlTree.toString();
      response.setHeader("Location", locationUrl);
      // console.log("301 Redirect Location:", locationUrl);
    }

    return new RedirectCommand(urlTree);
  }

  // If redirectTo is already a UrlTree or array, use it directly
  // Get current language from LanguageService
  const currentLang = languageService.currentLanguage();

  const navigationPath = Array.isArray(redirectTo)
    ? [currentLang, ...redirectTo]
    : [currentLang, redirectTo];
  const urlTree = router.createUrlTree(navigationPath);

  // Set Location header for proper 301 redirect
  if (response && typeof response.setHeader === "function") {
    const locationUrl = urlTree.toString();
    response.setHeader("Location", locationUrl);
    // console.log("301 Redirect Location:", locationUrl);
  }

  return new RedirectCommand(urlTree);
};
